import { z } from "zod";
import { paginationSchema } from "./pagination";

/**
 * HCA Moravia — validační schémata pro přípravné checklisty (plán §S.2.7).
 * Odlišné od `hca-products` discovery checklistu (50 otázek).
 */

export const checklistFazeEnum = z.enum(["PRED", "BEHEM", "PO"]);
export type ChecklistFaze = z.infer<typeof checklistFazeEnum>;

const uuidMsg = "Neplatné UUID";

/** Jedna položka checklistu — text, popis, povinné, odpovědná role. */
export const checklistItemSchema = z.object({
  text: z.string().min(1, "Text je povinný").max(500, "Text musí být 500 znaků nebo méně"),
  popis: z.string().max(2000, "Popis musí být 2000 znaků nebo méně").optional(),
  povinne: z.coerce.boolean().default(true),
  /**
   * Štítek odpovědné role („asistent", „přípravář"…) — volný text, ne konkrétní
   * osoba. Prázdný string se normalizuje na undefined (= bez role).
   */
  odpovednaRole: z
    .string()
    .trim()
    .max(50, "Role musí být 50 znaků nebo méně")
    .optional()
    .transform((v) => (v === "" ? undefined : v)),
});

/** Vytvoření šablony se seznamem items. */
export const createTemplateSchema = z.object({
  nazev: z.string().min(1, "Název je povinný").max(255, "Název musí být 255 znaků nebo méně"),
  popis: z.string().max(5000, "Popis musí být 5000 znaků nebo méně").optional(),
  faze: checklistFazeEnum,
  aktivni: z.coerce.boolean().default(true),
  items: z
    .array(checklistItemSchema)
    .min(1, "Šablona musí mít alespoň jednu položku")
    .max(100, "Maximálně 100 položek"),
});

/** Update šablony — pokud jsou `items`, replace-all. */
export const updateTemplateSchema = z.object({
  id: z.string().uuid(uuidMsg),
  nazev: z
    .string()
    .min(1, "Název je povinný")
    .max(255, "Název musí být 255 znaků nebo méně")
    .optional(),
  popis: z.string().max(5000, "Popis musí být 5000 znaků nebo méně").nullable().optional(),
  faze: checklistFazeEnum.optional(),
  aktivni: z.coerce.boolean().optional(),
  items: z.array(checklistItemSchema).max(100, "Maximálně 100 položek").optional(),
});

export const listTemplatesSchema = paginationSchema.extend({
  faze: checklistFazeEnum.optional(),
  aktivni: z.coerce.boolean().optional(),
  search: z.string().max(255, "Hledaný text musí být 255 znaků nebo méně").optional(),
});

export const duplicateTemplateSchema = z.object({
  id: z.string().uuid(uuidMsg),
  novyNazev: z.string().min(1, "Název je povinný").max(255, "Název musí být 255 znaků nebo méně"),
});

export const templateIdSchema = z.object({
  id: z.string().uuid(uuidMsg),
});

/** Instantiace šablony pro konkrétní termín — kopie items do instance. */
export const instantiateForTerminSchema = z.object({
  templateId: z.string().uuid(uuidMsg),
  terminId: z.string().uuid(uuidMsg),
});

export const listInstancesByTerminSchema = z.object({
  terminId: z.string().uuid(uuidMsg),
});

export const instanceIdSchema = z.object({
  id: z.string().uuid(uuidMsg),
});

/** Odkliknutí (nebo zpětné odškrtnutí) jedné položky + volitelná poznámka. */
export const toggleInstanceItemSchema = z.object({
  instanceItemId: z.string().uuid(uuidMsg),
  odkliknuto: z.coerce.boolean(),
  poznamka: z.string().max(500, "Poznámka musí být 500 znaků nebo méně").nullable().optional(),
});

export const getInstanceProgressSchema = z.object({
  terminId: z.string().uuid(uuidMsg),
});

/** Výchozí šablony checklistů přiřazené produktu. */
export const listProductTemplatesSchema = z.object({
  productId: z.string().uuid(uuidMsg),
});

/**
 * Nastavení výchozích šablon produktu — replace-all: pole `templateIds`
 * je kompletní nový stav, prázdné pole odpojí všechny šablony.
 */
export const setProductTemplatesSchema = z.object({
  productId: z.string().uuid(uuidMsg),
  templateIds: z
    .array(z.string().uuid(uuidMsg))
    .max(50, "Maximálně 50 šablon na produkt")
    .default([]),
});

export type ChecklistItemInput = z.infer<typeof checklistItemSchema>;
export type CreateTemplateInput = z.infer<typeof createTemplateSchema>;
export type UpdateTemplateInput = z.infer<typeof updateTemplateSchema>;
export type ListTemplatesInput = z.infer<typeof listTemplatesSchema>;
export type DuplicateTemplateInput = z.infer<typeof duplicateTemplateSchema>;
export type TemplateIdInput = z.infer<typeof templateIdSchema>;
export type InstantiateForTerminInput = z.infer<typeof instantiateForTerminSchema>;
export type ListInstancesByTerminInput = z.infer<typeof listInstancesByTerminSchema>;
export type InstanceIdInput = z.infer<typeof instanceIdSchema>;
export type ToggleInstanceItemInput = z.infer<typeof toggleInstanceItemSchema>;
export type GetInstanceProgressInput = z.infer<typeof getInstanceProgressSchema>;
export type ListProductTemplatesInput = z.infer<typeof listProductTemplatesSchema>;
export type SetProductTemplatesInput = z.infer<typeof setProductTemplatesSchema>;
